import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  TextField,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  GetVlpReports,
  DeleteVlpReport,
  SaveVlpReport,
  UpdateVlpReport,
} from "../../../services/ReportSettingsService";
import CommonLoader from "../../common/CommonLoader";
import ViewReportSetting from "./ViewReportSetting";
import DeleteConfirmation from "../../common/DeleteConfirmation";
import AddReportDialog from "./AddReportDialog";
import { NotificationManager } from "react-notifications";

const ReportSettingsTable = () => {
  const columns = [
    "Report file name",
    "Description/Title",
    "Individual Order",
    "Parameter Prompts",
    "Report Type",
    "Update Orderstatus",
    "Label report",
    "Actions",
  ];
  const columnHeaders = [
    "msrsReport",
    "description",
    "individualOrder",
    "parameterPrompts",
    "reportType",
    "updateorderstatus",
    "labelreport",
  ];
  const rowsPerPageOptions = [10, 25, 50, 100, 200];

  const [reportsData, setReportsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedReportIdToDelete, setSelectedReportIdToDelete] =
    useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] =
    useState(false);
  const [isNewReportDialogOpen, setIsNewReportDialogOpen] = useState(false);
  const [reportToEdit, setReportToEdit] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await GetVlpReports("All");
      setReportsData(response.results);
      setFilteredData(response.results);
    } catch (error) {
      console.error("Error fetching reports:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const filteredResults = reportsData.filter((report) =>
      columnHeaders.some((header) =>
        String(report[header]).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    setFilteredData(filteredResults);
  }, [searchTerm, reportsData]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewClick = (report) => {
    setSelectedReport(report);
  };

  const handleBackClick = () => {
    setSelectedReport(null);
    setSearchTerm("");
  };

  const handleAddReportClick = () => {
    setReportToEdit(null); // Clear the report to edit when adding a new report
    setIsNewReportDialogOpen(true);
  };

  const handleEditClick = (report) => {
    setReportToEdit(report);
    setIsNewReportDialogOpen(true);
  };

  const handleDeleteClick = (reportId) => {
    setSelectedReportIdToDelete(reportId);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleNewReportDialogClose = () => {
    setIsNewReportDialogOpen(false); // Close the new sample dialog
  };

  const saveReport = async (report) => {
    try {
      const response = await SaveVlpReport(report);

      if (response.results) {
        NotificationManager.success("Report added successfully");
        setIsNewReportDialogOpen(false);
        fetchReports();
      } else {
        NotificationManager.error("Failed to save report:", response.message);
      }
    } catch (error) {
      NotificationManager.error("Failed to save report:", error.message);
    }
  };

  const updateReport = async (report) => {
    try {
      const response = await UpdateVlpReport(report);

      if (response.results) {
        NotificationManager.success("Report updated successfully");
        setIsNewReportDialogOpen(false);
        fetchReports();
      } else {
        NotificationManager.error("Failed to save report:", response.message);
      }
    } catch (error) {
      NotificationManager.error("Failed to save report:", error.message);
    }
  };

  const handleNewReportDialogAdd = (report) => {
    report.recordid === 0 ? saveReport(report) : updateReport(report);
  };

  const handleDeleteConfirmationConfirm = async () => {
    try {
      const response = await DeleteVlpReport(selectedReportIdToDelete);
      if (response.results === true) {
        NotificationManager.success("Report deleted successfully");
        setIsDeleteConfirmationOpen(false);
        fetchReports();
      } else {
        NotificationManager.error("Error deleting report");
      }
    } catch (error) {
      NotificationManager.error("Error deleting report");
    }
  };

  const renderActions = (report) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(report)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(report)}
      >
        <VisibilityIcon />
      </IconButton>
      <IconButton
        aria-label="delete"
        style={{ color: "red" }}
        onClick={() => handleDeleteClick(report.msrsReport)}
      >
        <DeleteIcon />
      </IconButton>
    </div>
  );

  return (
    <>
      <span className="title">Reports </span>
      <hr />
      {selectedReport ? (
        <>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            style={{ marginBottom: "16px", color: "green" }}
          >
            Back
          </Button>
          <ViewReportSetting report={selectedReport} />
        </>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              marginBottom: "16px",
              alignItems: "center",
            }}
          >
            <TextField
              label="Search"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginRight: "16px", width: "80%" }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddReportClick}
            >
              New
            </Button>
          </div>
          <Paper style={{ marginRight: "20px" }}>
            <TableContainer
              sx={{ maxHeight: 500, overflowY: "auto", width: "99%" }}
            >
              <Table sx={{ minWidth: 700 }} size="small" stickyHeader>
                <TableHead
                  sx={{
                    backgroundColor: (theme) => theme.palette.primary.main,
                    color: "white",
                  }}
                >
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell key={column}>{column}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((report, index) => (
                      <TableRow
                        key={report.msrsReport}
                        sx={{
                          backgroundColor:
                            index % 2 === 0 ? "rgba(0, 0, 0, 0.04)" : "white",
                        }}
                      >
                        {columnHeaders.map((column) => (
                          <TableCell key={column}>
                            {/* Conditionally render "Yes" or "No" based on the boolean value */}
                            {column === "individualOrder" ||
                              column === "parameterPrompts" ||
                              column === "updateorderstatus" ||
                              column === "labelreport"
                              ? report[column]
                                ? "Yes"
                                : "No"
                              : report[column]}
                          </TableCell>
                        ))}
                        <TableCell>{renderActions(report)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Paper>
          <CommonLoader loading={loading} />
          <DeleteConfirmation
            isOpen={isDeleteConfirmationOpen}
            onClose={handleDeleteConfirmationClose}
            onConfirm={handleDeleteConfirmationConfirm}
            title="Delete Confirmation"
            message="Are you sure you want to delete this report?"
            color="red"
          />
        </>
      )}
      <AddReportDialog
        open={isNewReportDialogOpen}
        onClose={handleNewReportDialogClose}
        onAdd={handleNewReportDialogAdd}
        report={reportToEdit}
      />
    </>
  );
};

export default ReportSettingsTable;
